<template>
        <div>
            <h3 v-if="!this.isAddEquipmentMode" class="editor-header px-2">
                {{ $t('fence.editor_title')  }}
            </h3>
            <location-deviation v-if="!this.isAddEquipmentMode" :location-deviation="item.location_deviation"/>
            <div class="px-2">
                <div class="row" v-if="!this.isAddEquipmentMode">
                    <div class="col-6">
                        <span class="span-title">{{ $t('common.id') }}</span>
                        <span class="item-detail-text">{{ item.external_system_id }}</span>
                    </div>
                    <div class="col-6">
                        <road-addess-view :road-number="item.road_num" :section-number="item.road_sec" :distance="item.distance" :show-street-name="false"/>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-4 pl-1">
                        <span class="span-title">{{ $t('fence.type') }}</span>
                        <select
                            v-model="item.type"
                            class="form-control form-control-sm"
                            :class="{ 'is-invalid': !this.validateIsNumber(item.type) }"
                        >
                            <option :value="null" disabled>{{ $t('post.select_type') }}</option>
                            <option
                                v-for="item in getFenceTypes()"
                                :key="item.value"
                                :value="item.value"
                            >
                                {{ item.name }}
                            </option>
                        </select>
                    </div>

                    <div class="col-sm-4 pl-1">
                        <span class="span-title">{{ $t('fence.post_material') }}</span>
                        <select
                            v-model="item.post_material"
                            class="form-control form-control-sm"
                        >
                            <option :value="null" disabled>{{ $t('fence.select_post_material') }}</option>
                            <option
                                v-for="item in getMaterials()"
                                :key="item.value"
                                :value="item.value"
                            >
                                {{ item.name }}
                            </option>
                        </select>
                    </div>

                    <div class="col-sm-4 pl-1">
                        <condition-selector v-model="item.condition" :required="true" />
                    </div>
                </div>
                <div class="row">
                    <location-details
                        v-if="!this.isAddEquipmentMode"
                        :lane-data="item.location_specification.lane"
                        :side-data="item.location_specification.side"
                        :track-data="item.location_specification.tracks"
                    />
                </div>
                <div class="row">
                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('railing.material') }}</span>
                        <select
                            v-model="item.material"
                            class="form-control form-control-sm"
                        >
                            <option :value="null" disabled>{{ $t('railing.select_material') }}</option>
                            <option
                                v-for="item in getMaterials()"
                                :key="item.value"
                                :value="item.value"
                            >
                                {{ item.name }}
                            </option>
                        </select>
                    </div>

                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('post.height') }}</span>
                        <input
                            id="height"
                            v-model="item.height"
                            type="number"
                            class="form-control form-control-sm"
                            min="1"
                        />
                    </div>

                    <div class="col-sm-4">
                        <span class="span-title">{{ $t('post.code') }}</span>
                        <input
                            id="code"
                            v-model="item.code"
                            type="text"
                            class="form-control form-control-sm"
                        />
                    </div>
                </div>
                <template v-if="this.isAddEquipmentMode">
                    <div class="row">
                        <div class="col-sm-4">
                            <span class="span-title">{{ $t('velho_equipment.starting_position') }}</span>
                        </div>
                        <div class="col-sm-12">
                            <h4 class="form-header nopads mt-0">{{ $t('velho_equipment.location_spesifications') }}</h4>
                            <velho-equipment-location-details-editor
                                :hideRampSelector="true"
                                :hideShoulderSelector="true"
                                :hidePlatformSelector="true"
                                :hideEdgeAreaSelector="true"
                                :hideDividerSelector="true"
                                :hideCenterAreaSelector="true"
                                :hideDitchBottomSelector="true"
                                :roadInfo="item"
                                :locationSpecification="item.location_specification"
                                :tracksRequired="false"
                                :hideTracksSelector="true"
                            />
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-sm-4">
                            <span class="span-title">{{ $t('velho_equipment.ending_position') }}</span>
                        </div>
                        <div class="col-sm-12 nopads">
                            <velho-equipment-location-details-editor
                                :hideRampSelector="true"
                                :hideShoulderSelector="true"
                                :hidePlatformSelector="true"
                                :hideEdgeAreaSelector="true"
                                :hideDividerSelector="true"
                                :hideCenterAreaSelector="true"
                                :hideDitchBottomSelector="true"
                                :roadInfo="item.location_ending"
                                :tracksRequired="false"
                                :hideTracksSelector="true"
                                :sideRequired="false"
                                :hideSideSelector="true"
                            />
                        </div>
                    </div>
                </template>
                <hr class="mb-0">
                <div class="row" v-if="this.isAddEquipmentMode">
                    <div class="col-sm-6 nopads mt-2">
                        <equipment-date
                            :text="$t('velho_equipment.start_date')"
                            v-model="item.created_at"
                        />
                    </div>
                </div>
                <div class="row">
                    <equipment-actions v-if="!this.isAddEquipmentMode" ref="actions" v-model="item.actions" />
                </div>
                <div class="row">
                    <linked-equipment
                        v-if="item.linked_equipment && item.linked_equipment.length > 0"
                        :linked-equipments="item.linked_equipment"
                    />
                </div>
            </div>
            <velho-editor-buttons
                v-if="!this.isAddEquipmentMode"
                :url="extFencesUrl"
                :selected-contract="selectedContract"
                :item="item"
                :disabled="loading"
                @close="close"
                @submit="handleSubmit"
                @removalConfirmed="removalConfirmed"
            />
        <div
            v-if="loading > 0"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {mapHelper} from "@/components/mixins/MapMixin";
import {fenceHelper} from "@/components/mixins/FenceMixin";
import {conversionModelHelper} from "@/components/mixins/ConversionMixin";
import {restApi} from "@/components/mixins/RestApiMixin";
import ConditionSelector from "@/components/velho/ConditionSelector";
import {velhoHelper} from "@/components/mixins/VelhoMixin";
import EquipmentActions from "@/components/velho/EquipmentActions";
import VelhoEditorButtons from "@/components/velho/VelhoEditorButtons";
import LinkedEquipment from "@/components/velho/LinkedEquipment";
import LocationDetails from "@/components/velho/LocationDetails.vue";
import LocationDeviation from "@/components/velho/LocationDeviation";
import VelhoEquipmentLocationDetailsEditor from "@/components/velho/VelhoEquipmentLocationDetailsEditor";
import RoadAddessView from "@/components/velho/RoadAddressView";
import {velhoValidationMixin} from "@/components/mixins/VelhoValidationMixin";
import EquipmentDate from "@/components/velho/EquipmentDate.vue";

export default {
    name: 'FenceEditor',
    components: {
        EquipmentDate,
        RoadAddessView,
        LocationDeviation, VelhoEquipmentLocationDetailsEditor,
        LocationDetails, VelhoEditorButtons, EquipmentActions, ConditionSelector, LinkedEquipment},
    mixins: [velhoHelper, mapHelper, fenceHelper, conversionModelHelper, restApi, velhoValidationMixin],
    props: {
        item: {
            type: Object,
            default: null
        },
        selectedContract: {
            type: Object,
            default: null
        },
        addEquipmentEditor: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isAddEquipmentMode() {
            return this.addEquipmentMode
        }
    },
    data() {
        return {
            addEquipmentMode: this.addEquipmentEditor,
            map: null,
            loading: false,
        }
    },
    methods: {
        handleSubmit: function(url, item, selectedContract, replace = false) {
            if(replace) {
                this.submitEquipmentsreplace(this.extEquipmentsReplaceUrl, [item], selectedContract)
            } else {
                this.submitEquipment(url, item, selectedContract);
            }
        },
        close: function () {
            this.$emit('close')
        },
        isValidForm: function() {
            if(!this.item) { return false }
            if(!this.validateRoadNumber(this.item.road_num)) { return false }
            if(!this.validateRoadSection(this.item.road_sec)) { return false }
            if(!this.validateRoadDistance(this.item.distance)) { return false }
            if(!this.validateRoadSide(this.item.location_specification.side)) { return false }
            if(!this.validateRoadNumber(this.item.location_ending.road_num)) { return false }
            if(!this.validateRoadSection(this.item.location_ending.road_sec)) { return false }
            if(!this.validateRoadDistance(this.item.location_ending.distance)) { return false }
            if(!this.validateIsNumber(this.item.type)) { return false }
            if(!this.validateIsNumber(this.item.condition)) { return false }

            return true
        }
    }
}
</script>

<style scoped>
.map-container {
    height: 30em;
    position: relative;
}

</style>

